import defaults from '/src/utils/defaults';
import { useSessionStore } from "/src/stores/session"
import { useAppStore } from "/src/stores/app"
import axios from 'axios'
import bus from '/src/utils/event_bus'


const backend_api = axios.create({
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
    baseURL: defaults.ROOT_API,
    validateStatus: function (status) {
        return status >= 200 && status < 300; // default
    },
});
backend_api.interceptors.request.use(config => {
    let session_store
    try {
        session_store = useSessionStore()
    } catch {
        console.log("[🍍] Pinia not initialized. Sending NO_AUTH request.")
        session_store = { authenticated: false }
    }
    if (session_store.authenticated) {
        config['headers']['Authorization'] = 'Bearer ' + session_store.access_token
    } else {
        config['headers']['Authorization'] = 'NO_AUTH'
    }
    // Issue #145: inyectar la selección global de compañías en todas las peticiones
    // del cliente externo (incluida la paginación). El backend la valida.
    if (session_store.is_external_customer && session_store.selected_companies_header) {
        config['headers']['X-Selected-Companies'] = session_store.selected_companies_header
    }
    return config
})
// Track authentication failures to prevent infinite loops
let authFailureCount = 0;
let lastAuthFailure = 0;
const MAX_AUTH_FAILURES = 10; // EMERGENCY FIX: Increased from 3 to 10
const AUTH_FAILURE_RESET_TIME = 30000; // EMERGENCY FIX: Reduced from 60s to 30s
let isLoggingOut = false; // EMERGENCY FIX: Prevent multiple simultaneous logouts

backend_api.interceptors.response.use(
    response => {
        const app_store = useAppStore()
        if (app_store.connection_lost) {
            app_store.setConnectionLost(false)
            bus.emit('notification', { timeout: 5000, text: "Connection recovered", color: "success" })
            bus.emit("back_event", { event: "connection_recovered", meta: {} })
        }
        
        // Reset auth failure count on successful response
        if (authFailureCount > 0) {
            authFailureCount = 0;
            console.log("[Auth] Authentication recovered, reset failure count");
        }
        
        return response;
    },
    error => {
        if (!error.response) {
            const app_store = useAppStore()
            if (!app_store.connection_lost) {
                app_store.setConnectionLost(true)
                bus.emit('notification', { timeout: 10000, text: "Error - Connection lost", color: "error" })
                bus.emit("back_event", { event: "connection_lost", meta: {} })
            }
            return Promise.reject(error);
        }
        
        // Handle authentication errors (401 and 403)
        if (error?.response?.status === 401 || 
            (error?.response?.status === 403 && error.response?.data?.code === "token_not_valid")) {
            
            const now = Date.now();
            
            // Reset failure count if enough time has passed
            if (now - lastAuthFailure > AUTH_FAILURE_RESET_TIME) {
                authFailureCount = 0;
            }
            
            authFailureCount++;
            lastAuthFailure = now;
            
            console.warn(`[Auth] Authentication failure ${authFailureCount}/${MAX_AUTH_FAILURES}`, {
                status: error.response.status,
                url: error.config?.url,
                code: error.response?.data?.code
            });
            
            // If we've exceeded max failures, force logout immediately
            if (authFailureCount >= MAX_AUTH_FAILURES) {
                console.error("[Auth] Max authentication failures reached, forcing logout");
                
                bus.emit('notification', { 
                    timeout: 8000, 
                    text: "Session expired due to multiple authentication failures. Please log in again.", 
                    color: "error" 
                });
                
                // Force immediate logout without trying to refresh
                bus.emit("events/session/logout");
                return Promise.reject(error);
            }
            
            // For 403 with token_not_valid, try to refresh token (only if not too many failures)
            if (error?.response?.status === 403 && error.response?.data?.code === "token_not_valid") {
                const session_store = useSessionStore();
                
                // Only attempt refresh if we haven't failed too many times
                if (authFailureCount < MAX_AUTH_FAILURES) {
                    console.log("[Auth] Attempting token refresh");
                    session_store.refresh().catch(() => {
                        // If refresh fails, count as another failure and potentially logout
                        authFailureCount++;
                        if (authFailureCount >= MAX_AUTH_FAILURES) {
                            bus.emit("events/session/logout");
                        }
                    });
                }
                return Promise.reject(error);
            }
            
            // For 401 errors, show notification and logout
            if (error?.response?.status === 401) {
                // EMERGENCY FIX: Prevent multiple simultaneous logouts
                if (!isLoggingOut) {
                    isLoggingOut = true;
                    console.log("[Auth] 401 detected, initiating logout");
                    
                    bus.emit('notification', { 
                        timeout: 8000, 
                        text: "Session expired. Please log in again.", 
                        color: "error" 
                    });
                    
                    bus.emit("events/session/logout");
                    
                    // Reset flag after a delay
                    setTimeout(() => {
                        isLoggingOut = false;
                    }, 5000);
                } else {
                    console.log("[Auth] Logout already in progress, skipping");
                }
                return Promise.reject(error);
            }
        }
        else if (error?.response?.status == 403 && error?.response?.data?.detail == "You do not have permission to perform this action.") {
            console.log("[!] Permission error. Check the Network tab in the browser.")
            // Show a more detailed error message to the user
            bus.emit('notification', { 
                timeout: 10000, 
                text: "Permission denied: You do not have the necessary permissions to perform this action.", 
                color: "error" 
            })
            return Promise.reject(error)
        }
        else if (error?.response?.status == 403) {
            // Show a more detailed error message before logout
            bus.emit('notification', { 
                timeout: 10000, 
                text: "Authentication error: Your session is invalid or has expired. You will be logged out.", 
                color: "error" 
            })
            bus.emit("events/session/logout")
        }
        else if (error?.response?.status >= 500) {
            // Show a more detailed server error message
            const errorMessage = error.response?.data?.detail || 
                                error.response?.data?.message || 
                                `Server error (${error.response.status}): The server encountered an error processing your request.`;
            bus.emit('notification', { 
                timeout: 10000, 
                text: errorMessage, 
                color: "error" 
            })
        }
        else if (error?.response?.status == 400) {
            // Extract error details from the response if available
            let errorMessage = "Bad request: The server could not process your request.";
            if (error.response?.data) {
                // Try to extract error messages from the response data
                const errorData = error.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (typeof errorData === 'object') {
                    // Extract the first error message from the object
                    const firstErrorKey = Object.keys(errorData)[0];
                    if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) {
                        errorMessage = `${firstErrorKey}: ${errorData[firstErrorKey][0]}`;
                    } else if (firstErrorKey) {
                        errorMessage = `${firstErrorKey}: ${errorData[firstErrorKey]}`;
                    }
                }
            }
            bus.emit('notification', { 
                timeout: 10000, 
                text: errorMessage, 
                color: "error" 
            })
        }
        return Promise.reject(error)
    }
)


// returns a complete promise — also captures top-level extra keys (e.g. stats)
function paginated_factory(url) {
    let topLevelExtra = {}
    function paginatedFetcher(next, obj_list = []) {
        return backend_api.get(next ? next : url)
            .then(({ data }) => {
                // On first page, capture any extra top-level keys (e.g. stats, summary)
                if (obj_list.length === 0) {
                    const { results, count, next: _next, previous, ...rest } = data
                    topLevelExtra = rest
                }
                obj_list.push(...data.results)
                if (!data.next) return obj_list
                return paginatedFetcher(data.next, obj_list)
            })
    }
    return paginatedFetcher().then(data => ({ data, ...topLevelExtra }))
}


// Función para hacer logout en el backend
function logoutFromBackend() {
    return axios.post(defaults.ROOT_API + 'auth/logout/')
        .then(() => console.log("Cleaned session"))
        .catch(error => console.error("Error during logout:", error))
}

export {
    backend_api,
    paginated_factory,
    logoutFromBackend
};
